import { theme } from "@/common/Mixin"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin"
import GradientTop from "@/common/assets/image/products/nft/airdrop/Top.svg"
import EnvelopTop from "@/common/assets/image/Mainpage/hi/Top.svg"
import React, { ReactNode, useEffect, useLayoutEffect, useRef } from "react"
import styled, { useTheme } from "styled-components"
import NextImg from "../NextImg"

interface HorizontalScrollProps {
  defaultItemWidth?: number
  defaultItemGap?: number
  title?: string
  items: React.ReactNode[]
  backgroundType?: "gradient" | "envelope" | "none"
  scrollLengthFactor?: number
}

export default function HorizontalScroll({
  defaultItemWidth = 40,
  defaultItemGap = 5,
  title,
  items,
  backgroundType = "gradient",
  scrollLengthFactor = 7
}: HorizontalScrollProps) {
  let itemWidth = defaultItemWidth
  let itemGap = defaultItemGap
  let wrapperPadding = 8
  const theme = useTheme()
  gsap.registerPlugin(ScrollToPlugin, ScrollTrigger)

  const scopeRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const totalScrollWidth =
    itemWidth * items.length + itemGap * (items.length - 1) + wrapperPadding * 2 // vw units
  const useIsomorphicLayoutEffect =
    typeof window !== "undefined" ? useLayoutEffect : useEffect
  useIsomorphicLayoutEffect(() => {
    if (!scopeRef.current || !containerRef.current) return

    const mm = gsap.matchMedia()
    mm.add(`(min-width:${theme.lgWidth}px)`, () => {
      const panels = gsap.utils.toArray(".ass")

      const scrollWidth = containerRef.current!.scrollWidth - window.innerWidth // In pixels

      gsap.to(panels, {
        x: -scrollWidth,
        ease: "none",
        scrollTrigger: {
          trigger: scopeRef.current,
          pin: true,
          scrub: 1,
          end: () => "+=" + totalScrollWidth * scrollLengthFactor + "vw"
        }
      })
    })
    return () => mm.revert()
  }, [])

  return (
    <LayoutWrapper ref={scopeRef}>
      <ContentWrapper backgroundType={backgroundType}>
        <TopWrapper>
          <NextImg
            src={backgroundType === "gradient" ? GradientTop : EnvelopTop}
          />
        </TopWrapper>
        <VerticalWrapper>
          {title && <TitleWrapper>{title}</TitleWrapper>}
          <HorizontalWrapper
            backgroundType={backgroundType}
            width={totalScrollWidth}
            gap={itemGap}
            className="kg-wrapper"
            ref={containerRef}
          >
            {items.map((item: ReactNode, index: number) => {
              return (
                <ItemWrapper itemWidth={itemWidth} key={index} className="ass">
                  {item}
                </ItemWrapper>
              )
            })}
          </HorizontalWrapper>
        </VerticalWrapper>
        <BottomWrapper>
          <NextImg
            src={backgroundType === "gradient" ? GradientTop : EnvelopTop}
          />
        </BottomWrapper>
      </ContentWrapper>
    </LayoutWrapper>
  )
}

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
`

interface WrapperProps {
  backgroundType: "gradient" | "envelope" | "none"
}

const ContentWrapper = styled.div<WrapperProps>`
  background: ${({ backgroundType }) => {
    switch (backgroundType) {
      case "gradient":
        return theme.gradient.bg
      case "envelope":
        return theme.color.secondaryLight
      default:
        return "transparent"
    }
  }};
  display: flex;
  flex-direction: column;
  justify-content: center;
`

interface HorizontalWrapperProps {
  width: number
  gap: number
  backgroundType: "gradient" | "envelope" | "none"
}

const VerticalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;

  padding-top: 10vh;
  padding-bottom: 10vh;
`

const TitleWrapper = styled.h2`
  padding-left: 8vw;
  color: ${theme.color.primary};
`

const HorizontalWrapper = styled.div<HorizontalWrapperProps>`
  width: ${({ width }) => width}vw;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ gap }) => gap}vw;
  flex-wrap: nowrap;
`

const ItemWrapper = styled.div<{ itemWidth: number }>`
  width: ${({ itemWidth }) => itemWidth}vw;
  display: flex;
  height: 100%;
  align-items: flex-start;
`

const TopWrapper = styled.div`
  margin-bottom: 3vh;
  width: 100%;
  transform: translateY(-1px);
  @media (${theme.lg}) {
    margin-bottom: 0vh;
  }
`

const BottomWrapper = styled.div`
  margin-top: 3vh;
  width: 100%;
  transform: rotate(180deg) translateY(-1px);
  @media (${theme.lg}) {
    margin-top: 0vh;
  }
`
